import React, { useMemo, useState } from "react";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import { IconButton, Menu, MenuItem } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import Badge from "@components/Badge/Badge";
import type { SequenceView } from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE, SEQUENCE_EDITABLE_STATUSES } from "../constants";
import { formatInstant } from "../utils/formatInstant";
import { sequenceStatusLabel } from "../utils/sequenceStatusLabel";
import { operationStatusBadgeType } from "../utils/statusBadgeType";
import OperationDataGrid from "./OperationDataGrid";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences`;
const text = (value: string | number | null | undefined) =>
  value === null || value === undefined ? "-" : String(value);

const RowMenu: React.FC<{
  sequence: SequenceView;
  editable: boolean;
  extra?: React.ReactNode;
  onOpen?: (sequence: SequenceView) => void;
  onUpdate: (sequence: SequenceView) => void;
  onRemove: (sequence: SequenceView) => void;
}> = ({ sequence, editable, extra, onOpen, onUpdate, onRemove }) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const choose = (action: (sequence: SequenceView) => void) => () => {
    setAnchor(null);
    action(sequence);
  };
  return (
    <>
      <IconButton
        size="small"
        aria-label="More actions"
        data-testid={`sequence-menu-${sequence.id}`}
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(event.currentTarget);
        }}
      >
        <MoreVertOutlinedIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {onOpen && (
          <MenuItem data-testid={`sequence-open-${sequence.id}-menu`} onClick={choose(onOpen)}>
            {t(`${L}.grid.open`)}
          </MenuItem>
        )}
        {editable && (
          <MenuItem data-testid={`sequence-update-${sequence.id}`} onClick={choose(onUpdate)}>
            {t(`${L}.grid.update`)}
          </MenuItem>
        )}
        {editable && (
          <MenuItem data-testid={`sequence-remove-${sequence.id}`} onClick={choose(onRemove)}>
            {t(`${L}.grid.remove`)}
          </MenuItem>
        )}
        {extra}
      </Menu>
    </>
  );
};

const SequencesGrid: React.FC<{
  sequences: SequenceView[];
  operationStatus: string | null;
  readOnly?: boolean;
  onUpdate: (sequence: SequenceView) => void;
  onRemove: (sequence: SequenceView) => void;
  onOpen?: (sequence: SequenceView) => void;
  openInMenu?: boolean;
  rowActions?: (sequence: SequenceView) => React.ReactNode;
  extraColumns?: GridColDef[];
}> = ({
  sequences,
  operationStatus,
  readOnly = false,
  onUpdate,
  onRemove,
  onOpen,
  openInMenu = false,
  rowActions,
  extraColumns = [],
}) => {
  const { t } = useTranslation();
  const editable =
    !readOnly && operationStatus !== null && SEQUENCE_EDITABLE_STATUSES.includes(operationStatus);

  const columns = useMemo<GridColDef[]>(() => {
    const text13 = (field: string, key: string, flex: number, minWidth: number): GridColDef => ({
      field,
      headerName: t(`${L}.grid.${key}`),
      flex,
      minWidth,
    });
    const base: GridColDef[] = [
      onOpen
        ? {
            field: "sequenceNo",
            headerName: t(`${L}.grid.no`),
            flex: 0.5,
            minWidth: 48,
            renderCell: (params: GridRenderCellParams) => (
              <button
                type="button"
                className="underline ml-3"
                data-testid={`sequence-open-${params.row.id}`}
                aria-label={t(`${L}.sequence-detail.open`, { number: params.value })}
                onClick={(event) => {
                  event.stopPropagation();
                  const sequence = sequences.find((item) => item.id === params.row.id);
                  if (sequence) onOpen(sequence);
                }}
              >
                {params.value}
              </button>
            ),
          }
        : text13("sequenceNo", "no", 0.5, 48),
      text13("hold", "hold", 0.5, 52),
      text13("plannedQuantity", "planned-quantity", 1, 80),
      text13("gradeSizeCode", "grade-size", 0.9, 74),
      text13("cargoCode", "cargo-code", 0.9, 68),
      text13("stockPileCode", "stock-pile", 0.8, 62),
      text13("shipperCompanyCode", "shipper", 0.9, 64),
      text13("actualSize", "actual-size", 0.9, 72),
      text13("ats", "ats", 0.9, 110),
      text13("atc", "atc", 0.9, 110),
      {
        field: "status",
        headerName: t(`${L}.grid.status`),
        flex: 1.6,
        minWidth: 185,
        renderCell: (params: GridRenderCellParams) => (
          <Badge
            type={operationStatusBadgeType(params.row.status)}
            label={params.row.statusLabel}
            testId={`badge-${params.row.id}`}
          />
        ),
      },
    ];
    base.push(...extraColumns);
    if (editable || rowActions || openInMenu) {
      base.push({
        field: "action",
        headerName: t("common.action-column"),
        width: 64,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams) => {
          const sequence = sequences.find((item) => item.id === params.row.id);
          return sequence ? (
            <RowMenu
              sequence={sequence}
              editable={editable}
              extra={rowActions?.(sequence)}
              onOpen={openInMenu ? onOpen : undefined}
              onUpdate={onUpdate}
              onRemove={onRemove}
            />
          ) : null;
        },
      });
    }
    return base;
  }, [t, editable, sequences, onUpdate, onRemove, onOpen, openInMenu, rowActions, extraColumns]);

  const rows = useMemo(
    () =>
      sequences.map((sequence) => ({
        id: sequence.id,
        sequenceNo: sequence.sequenceNo,
        hold: sequence.hold,
        plannedQuantity: text(sequence.plannedQuantity),
        gradeSizeCode: text(sequence.gradeSizeCode),
        cargoCode: text(sequence.cargoCode),
        stockPileCode: text(sequence.stockPileCode),
        shipperCompanyCode: text(sequence.shipperCompanyCode),
        actualSize: text(sequence.actualSize),
        ats: formatInstant(sequence.ats),
        atc: formatInstant(sequence.atc),
        actualQuantity: text(sequence.actualQuantity),
        remainingQuantity: text(sequence.remainingQuantity),
        transporter: sequence.transporterCompanyName
          ? `${sequence.transporterCompanyCode} - ${sequence.transporterCompanyName}`
          : text(sequence.transporterCompanyCode),
        status: sequence.status,
        statusLabel: sequenceStatusLabel(t, sequence.status),
      })),
    [sequences, t],
  );

  return (
    <OperationDataGrid
      title={t(`${L}.grid.title`)}
      hideTitle
      testId="sequences-grid"
      columns={columns}
      plainFields={["action"]}
      rows={rows}
      emptyMessage={t(`${L}.grid.empty`)}
      onRowOpen={
        onOpen
          ? (id) => {
              const sequence = sequences.find((item) => item.id === id);
              if (sequence) onOpen(sequence);
            }
          : undefined
      }
    />
  );
};

export default SequencesGrid;
